import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
axios.defaults.withCredentials = true;

// Auto-Refresh: Access-Token läuft nach 15 Min ab. Bei 401 einmalig
// /auth/refresh aufrufen (Refresh-Token gilt 7 Tage) und den Request wiederholen.
let refreshPromise = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const url = cfg.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") ||
      url.includes("/auth/logout") || url.includes("/auth/register");
    if (status === 401 && !cfg._retry && !isAuthCall) {
      cfg._retry = true;
      try {
        refreshPromise = refreshPromise ||
          axios.post(`${API}/refresh`, {}, { withCredentials: true });
        await refreshPromise;
        refreshPromise = null;
        return axios(cfg);
      } catch {
        refreshPromise = null;
      }
    }
    return Promise.reject(error);
  }
);

const CampusAuthContext = createContext(null);

export function CampusAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async ({ email, password }) => {
    try {
      const res = await axios.post(
        `${API}/login`,
        { email: email.trim(), password },
        { withCredentials: true }
      );
      setUser(res.data);
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) return { ok: false, error: detail || "Zu viele Fehlversuche. Bitte später erneut versuchen." };
      if (status === 401) return { ok: false, error: "Ungültige Zugangsdaten." };
      return { ok: false, error: detail || "Login fehlgeschlagen. Bitte später erneut versuchen." };
    }
  };

  const register = async ({ email, password, name, role, organisation }) => {
    try {
      const res = await axios.post(
        `${API}/register`,
        { email: email.trim(), password, name, role, organisation },
        { withCredentials: true }
      );
      setUser(res.data);
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) return { ok: false, error: "Diese E-Mail ist bereits registriert." };
      if (status === 422) return { ok: false, error: "Bitte alle Felder gültig ausfüllen (E-Mail-Format, Passwort min. 8 Zeichen)." };
      return { ok: false, error: detail || "Registrierung fehlgeschlagen." };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/logout`, {}, { withCredentials: true }); } catch {}
    try { sessionStorage.removeItem("dh-pki-form-v1"); } catch {}
    setUser(null);
  };

  return (
    <CampusAuthContext.Provider value={{ user, ready, login, register, logout, refresh: fetchMe }}>
      {children}
    </CampusAuthContext.Provider>
  );
}

export function useCampusAuth() {
  const ctx = useContext(CampusAuthContext);
  if (!ctx) throw new Error("useCampusAuth must be used inside CampusAuthProvider");
  return ctx;
}
